import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Lock, FileText, Share2, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const HomePage = () => {
  return (
    <div className="relative overflow-hidden" data-testid="homepage">
      {/* Floating Hearts Background */}
      <FloatingHearts />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 xl:gap-20 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#E8A0BF]/10 rounded-full text-xs sm:text-sm text-[#C25E5E] mb-4 sm:mb-6">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Celebrate your love story</span>
              </div>
              
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2D2D2D] leading-tight mb-4 sm:mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Create Your
                <span className="block gradient-text">Love Certificate</span>
              </h1>
              
              <p className="text-sm sm:text-base md:text-lg text-[#5A5A5A] mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0">
                A permanent, timestamped proof of your bond. Create a beautiful certificate 
                that captures your love story forever.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/create">
                  <Button 
                    className="w-full sm:w-auto bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6 sm:px-8 py-4 sm:py-5 shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center gap-2 text-sm sm:text-base"
                    data-testid="hero-create-btn"
                  >
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                    Create Your Certificate
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto border-[#E8A0BF]/30 text-[#E8A0BF] hover:bg-[#E8A0BF]/5 rounded-full px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base"
                    data-testid="hero-learn-btn"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Hero Image / Certificate Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mt-8 lg:mt-0"
            >
              <div className="certificate-preview rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl p-5 sm:p-8 md:p-12 relative">
                {/* Decorative borders */}
                <div className="absolute left-2 sm:left-4 top-6 sm:top-8 bottom-6 sm:bottom-8 w-3 sm:w-4 space-y-2 sm:space-y-4">
                  <div className="h-8 sm:h-12 bg-[#B4D2E7] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#E8A0BF] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#B4D2E7] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#E8A0BF] rounded-sm" />
                </div>
                <div className="absolute right-2 sm:right-4 top-6 sm:top-8 bottom-6 sm:bottom-8 w-3 sm:w-4 space-y-2 sm:space-y-4">
                  <div className="h-8 sm:h-12 bg-[#B4D2E7] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#E8A0BF] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#B4D2E7] rounded-sm" />
                  <div className="h-8 sm:h-12 bg-[#E8A0BF] rounded-sm" />
                </div>

                <div className="text-center relative z-10 px-2 sm:px-4">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#2D2D2D] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Certificate of Love
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5A5A5A] mb-4 sm:mb-6 uppercase tracking-widest" style={{ fontFamily: "'Playfair Display', serif" }}>Official Declaration</p>
                  <p className="text-xs text-[#888888] mb-2">This certifies that</p>
                  <p className="font-script text-xl sm:text-2xl md:text-3xl text-[#C97F84] mb-2 sm:mb-4">Your Names Here</p>
                  <p className="text-xs text-[#888888] mb-2">Have declared their love on this day</p>
                  <div className="w-24 sm:w-32 h-[2px] bg-[#C97F84] mx-auto mb-2 sm:mb-4" />
                  <p className="text-xs sm:text-sm text-[#5A5A5A] italic max-w-xs mx-auto">
                    "Your beautiful love message will appear here..."
                  </p>
                  
                  {/* LoBe Logo - Simple like homepage navbar */}
                  <div className="mt-4 sm:mt-8 flex justify-center">
                    <div className="inline-flex items-center gap-2">
                      <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-[#E8A0BF] fill-[#E8A0BF]" />
                      <span style={{ fontFamily: "'Playfair Display', serif" }} className="font-bold text-base sm:text-lg">
                        <span className="text-[#2D2D2D]">Lo</span><span className="text-[#E8A0BF]">Be</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-16 sm:w-20 h-16 sm:h-20 bg-[#E8A0BF]/20 rounded-full blur-xl" />
              <div className="absolute -bottom-4 -left-4 w-12 sm:w-16 h-12 sm:h-16 bg-[#B4D2E7]/30 rounded-full blur-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#F4F1EA]/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-12 md:mb-16"
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-3 sm:mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why Choose LoBe?
            </h2>
            <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
              A beautiful way to immortalize your love story with a unique, shareable certificate.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-hover bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${feature.bgColor} flex items-center justify-center mb-3 sm:mb-4`}>
                  <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${feature.iconColor}`} />
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-[#2D2D2D] mb-1 sm:mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#5A5A5A] line-clamp-3">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-12 md:mb-16"
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-3 sm:mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How It Works
            </h2>
            <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
              Create your love certificate in three simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center relative"
                data-testid={`step-${index + 1}`}
              >
                {/* Step number */}
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#E8A0BF]/10 text-[#E8A0BF] font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                  {index + 1}
                </div>
                
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-5 sm:top-6 left-[60%] w-[80%] h-px bg-[#E8A0BF]/30" />
                )}
                
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-[#2D2D2D] mb-1 sm:mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#5A5A5A]">{step.description}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10 sm:mt-12 md:mt-16"
          >
            <Link to="/create">
              <Button 
                className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6 sm:px-8 py-4 sm:py-5 shadow-lg hover:shadow-xl transition-all group flex items-center gap-2 mx-auto text-sm sm:text-base"
                data-testid="howit-create-btn"
              >
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                Start Creating Now
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#F4F1EA]/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-12 md:mb-16"
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-3 sm:mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Love Stories
            </h2>
            <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
              See what couples are saying about their LoBe certificates
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-3 sm:mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Heart key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-[#E8A0BF] text-[#E8A0BF]" />
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-[#5A5A5A] italic mb-3 sm:mb-4">"{testimonial.quote}"</p>
                <p className="text-xs sm:text-sm font-semibold text-[#2D2D2D]">{testimonial.name}</p>
                <p className="text-[10px] sm:text-xs text-[#888888]">{testimonial.location}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#FFFDF7] to-[#FCEFF4] rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden"
          >
            {/* Decorative hearts */}
            <div className="absolute top-4 left-4 text-2xl sm:text-4xl text-[#E8A0BF]/20">♥</div>
            <div className="absolute bottom-4 right-4 text-2xl sm:text-4xl text-[#E8A0BF]/20">♥</div>
            
            <h2 
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#2D2D2D] mb-3 sm:mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready to Celebrate Your Love?
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-[#5A5A5A] mb-6 sm:mb-8 max-w-xl mx-auto">
              Create a beautiful, permanent proof of your bond today. 
              Share it with the world or keep it as your precious keepsake.
            </p>
            <Link to="/create">
              <Button 
                className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6 sm:px-8 py-4 sm:py-5 shadow-lg hover:shadow-xl animate-pulse-glow group flex items-center gap-2 mx-auto text-sm sm:text-base"
                data-testid="final-create-btn"
              >
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 fill-white group-hover:scale-110 transition-transform" />
                Create Your Love Certificate
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Floating Hearts Component
const FloatingHearts = () => {
  const hearts = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    size: Math.random() * 15 + 8,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 4,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {hearts.map((heart) => (
        <div
          key={heart.id}
          className="absolute text-[#E8A0BF] opacity-10"
          style={{
            left: `${heart.left}%`,
            top: `${Math.random() * 100}%`,
            fontSize: `${heart.size}px`,
            animation: `float ${heart.duration}s ease-in-out infinite`,
            animationDelay: `${heart.delay}s`,
          }}
        >
          ♥
        </div>
      ))}
    </div>
  );
};

// Data
const features = [
  {
    icon: Lock,
    title: "Permanent Record",
    description: "Your certificate is timestamped and preserved forever. Once created, it cannot be altered.",
    bgColor: "bg-[#B4D2E7]/20",
    iconColor: "text-[#7AACC9]",
  },
  {
    icon: FileText,
    title: "Beautiful Image",
    description: "Download a stunning high-quality certificate image, perfect for printing and sharing.",
    bgColor: "bg-[#E8A0BF]/20",
    iconColor: "text-[#E8A0BF]",
  },
  {
    icon: Share2,
    title: "Easy Sharing",
    description: "Share your proof page via WhatsApp, Instagram, or any platform with a unique link.",
    bgColor: "bg-[#D4AF37]/20",
    iconColor: "text-[#D4AF37]",
  },
  {
    icon: Heart,
    title: "Made with Love",
    description: "Every certificate is a work of art, designed to capture the essence of your bond.",
    bgColor: "bg-[#C25E5E]/20",
    iconColor: "text-[#C25E5E]",
  },
];

const steps = [
  {
    title: "Fill Your Details",
    description: "Enter your name, your partner's name, and a heartfelt message (up to 50 words).",
  },
  {
    title: "Complete Payment",
    description: "Make a one-time payment to activate your certificate. No subscriptions, ever.",
  },
  {
    title: "Share & Download",
    description: "Get your unique proof page link and download your beautiful certificate.",
  },
];

const testimonials = [
  {
    name: "Sarah & Michael",
    location: "New York, USA",
    quote: "We created our LoBe certificate on our first anniversary. It's now framed on our wall!",
  },
  {
    name: "Priya & Raj",
    location: "Mumbai, India",
    quote: "Such a beautiful way to commemorate our love. The certificate design is absolutely stunning.",
  },
  {
    name: "Emma & James",
    location: "London, UK",
    quote: "Shared our certificate with family and friends. Everyone loved how unique and personal it was.",
  },
];

export default HomePage;
